import Image from "next/image";
import tamiLogoLight from "@/assets/svgs/tami-square-simple-light.svg";
import { HOME_COPY } from "../home.copy";
import { hidesPrice, resolveCtaLabel } from "@/lib/plans/price-on-request";
import {
  SHOWCASE_FEATURES,
  formatShowcaseLimit,
} from "@/lib/plans/showcase-features";
import { cn } from "@/lib/utils";
import { PLAN_CYCLE, type Plan } from "@/services/plans/types";
import { PlanFeatureChip } from "./plan-feature-chip";
import { PlanFeatureList } from "./plan-feature-list";
import { PlanInquiryModal } from "@/components/plan-inquiry/plan-inquiry-modal";
import { PriceOnRequestBlock } from "./price-on-request-block";
import { TamiButton } from "@/components/tami-button";

/**
 * A faixa de cor no topo identifica o degrau da escada. Só cores da paleta
 * Tãmi — nenhuma inventada para esta seção.
 */
export type PlanAccent = "pale" | "orange" | "green";

const ACCENT_STRIP: Record<PlanAccent, string> = {
  pale: "bg-tami-pale-green",
  orange: "bg-tami-orange",
  green: "bg-tami-green",
};

const ACCENT_BADGE: Record<PlanAccent, string> = {
  pale: "bg-tami-pale-green text-tami-dark",
  orange: "bg-tami-orange text-tami-dark",
  green: "bg-tami-green text-tami-dark",
};

interface PlanPricingCardProps {
  plan: Plan;
  highlighted: boolean;
  accent: PlanAccent;
  badgeLabel: string;
  pitch: string;
  ctaLabel: string;
  /** A lista de opcionais abre nas três colunas de uma vez — ver `PlanFeatureList`. */
  featuresExpanded: boolean;
  onToggleFeatures: () => void;
}

/** "R$ 87,00" → { integer: "87", decimal: "00" }. */
function splitPrice(value: string | null): { integer: string; decimal: string } {
  if (!value) return { integer: "—", decimal: "" };
  const [integer, decimal] = value
    .replace(/R\$\s*/, "")
    .trim()
    .split(",");
  return { integer: integer || "0", decimal: decimal || "00" };
}

/**
 * O card de um plano na vitrine.
 *
 * Substitui `FreePricingCard` e `TamiMaisPricingCard`, que eram dois
 * componentes quase idênticos com listas de features escritas à mão. Era essa
 * duplicação que sustentava o bug: dois componentes, duas regras de seleção, e
 * nenhuma delas sobreviveu ao terceiro plano.
 *
 * Os opcionais vêm de `SHOWCASE_FEATURES` (o que mostrar e como chamar) cruzado
 * com `plan.entitlements` (quanto). Feature que o plano não declara não some da
 * lista: ela aparece desligada, porque "não tem" é informação de compra.
 */
export const PlanPricingCard = ({
  plan,
  highlighted,
  accent,
  badgeLabel,
  pitch,
  ctaLabel,
  featuresExpanded,
  onToggleFeatures,
}: PlanPricingCardProps) => {
  const priceHidden = hidesPrice(plan);
  // Sob consulta o valor chega nulo sem o plano ser grátis. Ver
  // `presentPlan`, que aplica a mesma distinção ao badge e ao CTA.
  const isFree = !plan.priceOnRequest && !plan.unitValueNumber;
  const price = splitPrice(plan.unitValueWithCoupon ?? plan.unitValue);

  const chips = SHOWCASE_FEATURES.map((feature) => {
    const resolved = plan.entitlements[feature.key];
    if (!resolved) return null;

    return (
      <PlanFeatureChip
        key={feature.key}
        label={feature.label}
        limit={formatShowcaseLimit(plan.entitlements, feature)}
        enabled={resolved.enabled}
      />
    );
  }).filter(Boolean);

  return (
    <div className="flex h-full flex-col">
      {/* Slot reservado nas três colunas: a tarja do destaque não pode empurrar
          uma coluna para cima e quebrar a linha de base das outras. */}
      <div className="mb-2.5 h-8">
        {highlighted && (
          <span className="inline-block rounded-full bg-tami-green px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-tami-dark">
            {HOME_COPY.planCard.mostChosen}
          </span>
        )}
      </div>

      <div
        className={cn(
          "flex flex-1 flex-col overflow-hidden rounded-[28px] border-2 bg-tami-dark md:rounded-[40px]",
          highlighted
            ? "border-tami-green shadow-[0_0_0_6px_theme(colors.tami.green/9%),0_8px_24px_theme(colors.black/45%)]"
            : "border-tami-light shadow-[0_4px_8px_theme(colors.tami.dark/30%)]",
        )}
      >
        <div className={cn("h-3", ACCENT_STRIP[accent])} />

        <div className="flex flex-1 flex-col p-5 md:p-6">
          <div className="mb-4 flex items-start justify-between">
            <span
              className={cn(
                "whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold",
                ACCENT_BADGE[accent],
              )}
            >
              {badgeLabel}
            </span>
            <Image
              src={tamiLogoLight}
              alt=""
              width={36}
              height={35}
              className="flex-shrink-0 object-contain"
            />
          </div>

          <h3 className="mb-2 text-2xl font-bold leading-tight tracking-tight text-tami-light md:text-[32px]">
            {plan.name}
          </h3>
          <p className="min-h-[48px] text-[13px] leading-relaxed text-tami-light/80 md:min-h-[60px] md:text-sm">
            {pitch}
          </p>

          <div className="my-3.5 flex min-h-[80px] flex-col justify-center border-y-2 border-tami-light/20 py-3.5 md:my-4 md:min-h-[96px] md:py-4">
            {priceHidden ? (
              <PriceOnRequestBlock
                plan={plan}
                description={HOME_COPY.planCard.onRequestDescription}
              />
            ) : isFree ? (
              <>
                <span className="text-4xl font-bold leading-none tracking-tight text-tami-light md:text-[44px]">
                  {HOME_COPY.planCard.free}
                </span>
                <span className="mt-1.5 text-xs text-tami-light/65">
                  {HOME_COPY.planCard.freeForever}
                </span>
              </>
            ) : (
              <>
                <div className="flex items-baseline gap-0.5">
                  <span className="text-xl font-medium text-tami-light">R$</span>
                  <span className="text-4xl font-bold leading-none tracking-tight text-tami-light md:text-5xl">
                    {price.integer}
                  </span>
                  {price.decimal && (
                    <span className="self-start text-xl font-bold text-tami-light">
                      ,{price.decimal}
                    </span>
                  )}
                  <span className="ml-1.5 text-[15px] text-tami-light/75">
                    {plan.cycle === PLAN_CYCLE.ANNUAL
                      ? HOME_COPY.planCard.perYear
                      : HOME_COPY.planCard.perMonth}
                  </span>
                </div>
                {plan.priceDescription && (
                  <span className="mt-1.5 text-xs text-tami-light/65">
                    {plan.priceDescription}
                  </span>
                )}
              </>
            )}
          </div>

          {priceHidden ? (
            <PlanInquiryModal
              plan={plan}
              ctaLabel={resolveCtaLabel(plan)}
              className="w-full"
            />
          ) : (
            <TamiButton
              href="/sign-up"
              variant={highlighted ? "primary" : "light"}
              className={cn("w-full", accent === "pale" && "bg-tami-pale-green")}
            >
              {ctaLabel}
            </TamiButton>
          )}

          <div className="mb-2 mt-4 text-[11px] font-bold uppercase tracking-[0.1em] text-tami-light/55 md:mb-2.5 md:mt-5">
            {isFree
              ? HOME_COPY.planCard.includedFree
              : HOME_COPY.planCard.includedPaid}
          </div>
          <PlanFeatureList
            items={chips}
            expanded={featuresExpanded}
            onToggle={onToggleFeatures}
          />
        </div>
      </div>
    </div>
  );
};
